import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShieldCheck, FileText } from "lucide-react";
import { Logo } from "../components/Brand";
import { useApp, API } from "../AppContext";
import ProgereLegalFooter from "../components/ProgereLegalFooter";

const LegalShell = ({ children, icon: Icon, title }) => {
  const { lang, setLang } = useApp();
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "transparent" }}>
      <header className="sticky top-0 z-40">
        <div className="glass-card mx-3 sm:mx-4 mt-3 sm:mt-4 px-4 sm:px-6 py-3 flex items-center justify-between gap-3" style={{ borderRadius: 999 }}>
          <Link to="/"><Logo size="sm" /></Link>
          <div className="flex items-center gap-3">
            <button onClick={() => setLang(lang === "fr" ? "en" : "fr")} className="text-xs px-3 py-1 rounded-full border border-white/10 hover:border-[#D4AF37]" data-testid="legal-lang">
              {lang === "fr" ? "EN" : "FR"}
            </button>
            <Link to="/" className="btn-ghost text-xs">
              <ArrowLeft size={12} /> {lang === "fr" ? "Accueil" : "Home"}
            </Link>
          </div>
        </div>
      </header>
      <div className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.3)" }}>
            <Icon size={24} color="#D4AF37" />
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black" style={{ color: "#F5F5F5" }}>{title}</h1>
        </div>
        <div className="glass-card p-6 sm:p-10 prose prose-invert max-w-none">{children}</div>
      </div>
      <ProgereLegalFooter />
    </div>
  );
};

const Section = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="font-heading text-xl sm:text-2xl font-bold mb-3" style={{ color: "#D4AF37" }}>{title}</h2>
    <div className="text-sm leading-relaxed space-y-3" style={{ color: "rgba(245,245,245,0.82)" }}>{children}</div>
  </section>
);

export function TermsPage() {
  const { lang } = useApp();
  const [cfg, setCfg] = useState({});
  useEffect(() => { axios.get(`${API}/legal/config`).then(({ data }) => setCfg(data)); }, []);
  const T = lang === "fr" ? {
    title: "Conditions d'utilisation",
    updated: "Dernière mise à jour",
  } : {
    title: "Terms of Service",
    updated: "Last updated",
  };
  const today = "20 mai 2026";

  return (
    <LegalShell icon={FileText} title={T.title}>
      <p className="text-xs uppercase tracking-widest mb-6" style={{ color: "rgba(245,245,245,0.5)" }}>{T.updated} : {today} · v{cfg.terms_version || "1.0"}</p>

      <Section title="1. Acceptation des conditions">
        <p>En créant un compte sur <strong>{cfg.company_name}</strong> (« <strong>PROGÈRE Devis AI</strong> », l'« Application » ou « nous »), vous acceptez les présentes conditions d'utilisation. Si vous n'êtes pas d'accord avec ces conditions, vous ne devez pas utiliser l'Application.</p>
      </Section>

      <Section title="2. Description du service">
        <p>PROGÈRE Devis AI est une application SaaS d'estimation et de soumissions intelligentes destinée aux entreprises de construction du Québec. L'Application permet, entre autres :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>la génération de soumissions assistée par intelligence artificielle (Claude Sonnet 4.5) ;</li>
          <li>l'exportation de soumissions en format PDF ;</li>
          <li>la gestion de clients, projets et modèles personnalisés ;</li>
          <li>l'envoi de soumissions par courriel et le partage par lien public ;</li>
          <li>l'accès à un module ERP en option (sur demande).</li>
        </ul>
      </Section>

      <Section title="3. Plans, prix et inclusions">
        <p>Les plans suivants sont disponibles, facturés en <strong>dollars canadiens (CAD)</strong> :</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
          <div className="p-4 rounded-xl" style={{ background: "rgba(212,175,55,0.05)", border: "1px solid rgba(212,175,55,0.2)" }}>
            <div className="font-heading text-lg font-bold mb-2" style={{ color: "#F5F5F5" }}>Starter — 49 $ / mois</div>
            <ul className="text-xs space-y-1" style={{ color: "rgba(245,245,245,0.7)" }}>
              <li>20 soumissions par mois</li>
              <li>Génération IA de base</li>
              <li>Export PDF illimité</li>
              <li>1 utilisateur</li>
            </ul>
          </div>
          <div className="p-4 rounded-xl" style={{ background: "rgba(212,175,55,0.05)", border: "1px solid rgba(212,175,55,0.3)" }}>
            <div className="font-heading text-lg font-bold mb-2" style={{ color: "#D4AF37" }}>Pro — 149 $ / mois</div>
            <ul className="text-xs space-y-1" style={{ color: "rgba(245,245,245,0.7)" }}>
              <li>Soumissions illimitées</li>
              <li>IA avancée Claude Sonnet 4.5</li>
              <li>Templates personnalisés</li>
              <li>Jusqu'à 5 utilisateurs</li>
              <li>Suivi avancé et support prioritaire</li>
            </ul>
          </div>
        </div>
        <p><strong>Version d'essai gratuite</strong> : 3 soumissions de test par compte. <strong>Essai Pro 14 jours</strong> : disponible une seule fois par compte, sans carte de crédit.</p>
        <p>Le plan <strong>Entreprise / PROGÈRE ERP</strong> est offert sur devis personnalisé. Les <strong>modules additionnels</strong> (intégrations sur mesure, onboarding dédié, formation, modules ERP avancés) peuvent être facturés en supplément selon un devis distinct convenu par écrit.</p>
      </Section>

      <Section title="4. Renouvellement automatique">
        <p>Tous les abonnements (Starter, Pro) sont <strong>renouvelés automatiquement chaque mois</strong> tant que vous ne les annulez pas. Le renouvellement s'effectue à la même date que la souscription initiale et au tarif en vigueur lors du renouvellement. Vous recevrez un préavis par courriel en cas de changement de tarif au moins 30 jours avant son application.</p>
      </Section>

      <Section title="5. Annulation et fin d'abonnement">
        <p>Vous pouvez annuler votre abonnement à tout moment depuis la page « Abonnement » de votre tableau de bord. L'annulation prend effet à la fin de la période de facturation en cours — vous conservez l'accès aux fonctionnalités jusqu'à cette date.</p>
        <p>Aucun frais de résiliation ne s'applique. Aucun paiement partiel ne sera prélevé après l'annulation.</p>
      </Section>

      <Section title="6. Politique de remboursement">
        <p>Les paiements mensuels ne sont <strong>pas remboursables</strong>, sauf dans les cas suivants :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>erreur de facturation imputable à PROGÈRE ;</li>
          <li>indisponibilité majeure du service de plus de 72 heures consécutives non causée par votre fait ;</li>
          <li>obligation légale applicable au Québec ou au Canada.</li>
        </ul>
        <p>Toute demande de remboursement doit être faite par écrit à <a href={`mailto:${cfg.support_email}`} style={{ color: "#D4AF37" }}>{cfg.support_email}</a> dans les 14 jours suivant le paiement contesté.</p>
      </Section>

      <Section title="7. Utilisation responsable">
        <p>Vous vous engagez à utiliser l'Application uniquement à des fins légales et professionnelles. Il est interdit, sans s'y limiter, de :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>tenter d'accéder aux données d'autres utilisateurs ;</li>
          <li>contourner les limitations techniques (essai, quotas) ;</li>
          <li>utiliser l'IA pour générer du contenu illégal, diffamatoire ou trompeur ;</li>
          <li>revendre ou redistribuer l'accès au service sans autorisation écrite.</li>
        </ul>
      </Section>

      <Section title="8. Propriété intellectuelle">
        <p>L'Application, son code, son interface, ses textes et marques sont la propriété de {cfg.company_name}. <strong>Le contenu que vous créez</strong> (soumissions, clients, projets, photos) <strong>vous appartient</strong>. Vous nous accordez uniquement le droit de l'héberger et de le traiter pour vous fournir le service.</p>
      </Section>

      <Section title="9. Limitation de responsabilité">
        <p>L'Application est fournie « telle quelle » sans garantie d'aucune sorte. Dans les limites permises par la loi québécoise applicable, {cfg.company_name} ne peut être tenue responsable :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>des erreurs ou imprécisions des soumissions générées par l'IA (les tarifs CCQ, taxes et calculs doivent être validés par vous avant envoi à un client) ;</li>
          <li>des pertes indirectes, de profits, d'occasions d'affaires ou de réputation ;</li>
          <li>des interruptions de service dues à des cas de force majeure (panne tierce, sinistre, action gouvernementale) ;</li>
          <li>des conséquences d'une utilisation non conforme aux présentes conditions.</li>
        </ul>
        <p>En tout état de cause, notre responsabilité totale est limitée au montant payé par vous au cours des 12 derniers mois.</p>
      </Section>

      <Section title="10. Modifications">
        <p>Nous pouvons modifier ces conditions. En cas de modification importante, vous serez avisé par courriel et/ou dans l'Application au moins 30 jours avant l'entrée en vigueur. Votre utilisation continue après la date d'entrée en vigueur vaut acceptation.</p>
      </Section>

      <Section title="11. Loi applicable et juridiction">
        <p>Les présentes conditions sont régies par les lois en vigueur dans la province de <strong>Québec, Canada</strong>. Tout litige sera soumis à la compétence exclusive des tribunaux du district judiciaire de Québec, sauf renonciation expresse écrite des deux parties.</p>
      </Section>

      <Section title="12. Coordonnées">
        <p><strong>{cfg.company_name}</strong><br />
          {cfg.address}<br />
          Courriel général : <a href={`mailto:${cfg.support_email}`} style={{ color: "#D4AF37" }}>{cfg.support_email}</a><br />
          Confidentialité (Loi 25) : <a href={`mailto:${cfg.dpo_email}`} style={{ color: "#D4AF37" }}>{cfg.dpo_email}</a>
        </p>
      </Section>

      <div className="text-center mt-10 text-xs" style={{ color: "rgba(245,245,245,0.4)" }}>
        <Link to="/confidentialite" style={{ color: "#D4AF37" }}>{lang === "fr" ? "Voir la Politique de confidentialité" : "View Privacy Policy"}</Link>
      </div>
    </LegalShell>
  );
}

export function PrivacyPage() {
  const { lang } = useApp();
  const [cfg, setCfg] = useState({});
  useEffect(() => { axios.get(`${API}/legal/config`).then(({ data }) => setCfg(data)); }, []);
  const T = lang === "fr" ? {
    title: "Politique de confidentialité",
    updated: "Dernière mise à jour",
  } : {
    title: "Privacy Policy",
    updated: "Last updated",
  };

  return (
    <LegalShell icon={ShieldCheck} title={T.title}>
      <p className="text-xs uppercase tracking-widest mb-6" style={{ color: "rgba(245,245,245,0.5)" }}>{T.updated} : 20 mai 2026</p>

      <div className="p-4 rounded-xl mb-8" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.25)" }}>
        <p className="text-sm" style={{ color: "#F5F5F5" }}>
          <strong>Conformité Loi 25 (Québec)</strong> — Cette politique respecte la <em>Loi modernisant des dispositions législatives en matière de protection des renseignements personnels</em> (RLRQ, c. P-39.1). Elle décrit comment {cfg.company_name} recueille, utilise, protège et restitue vos renseignements personnels.
        </p>
      </div>

      <Section title="1. Responsable de la protection des renseignements personnels">
        <p>Conformément à la Loi 25, nous avons désigné un <strong>Responsable de la protection des renseignements personnels (RPRP)</strong> que vous pouvez contacter pour toute question relative à vos données :</p>
        <p className="p-4 rounded-lg" style={{ background: "rgba(245,245,245,0.04)", border: "1px solid rgba(212,175,55,0.15)" }}>
          📧 <a href={`mailto:${cfg.dpo_email}`} style={{ color: "#D4AF37" }}>{cfg.dpo_email}</a><br />
          📍 {cfg.company_name}, {cfg.address}
        </p>
      </Section>

      <Section title="2. Renseignements que nous recueillons">
        <p>Pour vous fournir le service, nous recueillons :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Identification</strong> : nom, courriel, nom de l'entreprise (lors de l'inscription) ;</li>
          <li><strong>Sécurité</strong> : mot de passe chiffré avec bcrypt (jamais en clair) ;</li>
          <li><strong>Données métier que vous saisissez</strong> : clients, projets, soumissions, modèles, photos, plans, paramètres fiscaux (TPS / TVQ) ;</li>
          <li><strong>Données de paiement</strong> : traitées exclusivement par <strong>Stripe</strong> — nous ne stockons jamais vos numéros de carte. Nous conservons uniquement l'identifiant de transaction, le montant et le statut ;</li>
          <li><strong>Données techniques</strong> : journaux applicatifs anonymisés (date/heure des appels API, codes d'erreur) à des fins de sécurité et de débogage ;</li>
          <li><strong>Communications</strong> : adresses courriel de vos clients lors d'un envoi de soumission ou partage de lien public.</li>
        </ul>
      </Section>

      <Section title="3. Finalités d'utilisation">
        <p>Vos renseignements sont utilisés <strong>uniquement</strong> pour :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>fournir, maintenir et améliorer le service ;</li>
          <li>générer des soumissions via l'IA (les requêtes envoyées au fournisseur d'IA ne contiennent pas vos identifiants personnels lorsque possible) ;</li>
          <li>traiter les abonnements et les paiements (via Stripe) ;</li>
          <li>vous envoyer des courriels transactionnels (confirmation, réinitialisation, expiration d'essai, notifications de soumission) via Resend ;</li>
          <li>respecter nos obligations légales ;</li>
          <li>protéger l'intégrité du service contre les abus.</li>
        </ul>
        <p>Nous ne <strong>vendons jamais</strong> vos renseignements à des tiers.</p>
      </Section>

      <Section title="4. Consentement">
        <p>Lors de votre inscription, vous donnez un <strong>consentement clair, libre et éclairé</strong> à la collecte et à l'utilisation de vos renseignements personnels aux fins décrites ci-dessus. Vous pouvez retirer votre consentement à tout moment en supprimant votre compte (voir section 8).</p>
        <p>Pour toute nouvelle finalité non couverte par la présente politique, nous solliciterons votre consentement spécifique.</p>
      </Section>

      <Section title="5. Communication à des tiers (sous-traitants)">
        <p>Nous faisons appel à des sous-traitants soigneusement sélectionnés pour fournir le service :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Stripe Inc.</strong> — traitement sécurisé des paiements (États-Unis, certifié PCI-DSS) ;</li>
          <li><strong>Resend Inc.</strong> — envoi de courriels transactionnels (États-Unis) ;</li>
          <li><strong>Anthropic / Emergent</strong> — fournisseur du modèle IA Claude Sonnet 4.5 ;</li>
          <li><strong>Hébergeur cloud</strong> — infrastructure d'hébergement.</li>
        </ul>
        <p>Conformément à l'<strong>article 17 de la Loi 25</strong>, nous nous assurons par contrat que ces sous-traitants offrent un <strong>niveau de protection équivalent</strong> à celui exigé au Québec. Certaines de ces communications peuvent impliquer un transfert de données <strong>hors du Québec</strong> — vous en êtes informé par la présente.</p>
      </Section>

      <Section title="6. Sécurité des données">
        <p>Nous mettons en œuvre des mesures techniques et organisationnelles raisonnables pour protéger vos données :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>chiffrement <strong>HTTPS / TLS</strong> de toutes les communications ;</li>
          <li>hachage des mots de passe avec <strong>bcrypt</strong> ;</li>
          <li>authentification par <strong>jeton JWT</strong> à expiration ;</li>
          <li>isolation stricte des données par utilisateur (chaque requête est filtrée par <code>user_id</code>) ;</li>
          <li>accès aux serveurs limité au personnel autorisé ;</li>
          <li>journalisation des actions sensibles ;</li>
          <li>sauvegardes régulières.</li>
        </ul>
      </Section>

      <Section title="7. Incident de confidentialité (fuite de données)">
        <p>Conformément à l'<strong>article 3.5 de la Loi 25</strong>, en cas d'incident de confidentialité présentant un <strong>risque sérieux de préjudice</strong>, nous :</p>
        <ol className="list-decimal pl-6 space-y-1">
          <li>prenons sans délai les mesures raisonnables pour en diminuer les risques ;</li>
          <li>avisons la <strong>Commission d'accès à l'information du Québec (CAI)</strong> ;</li>
          <li>avisons les personnes concernées par courriel dans les meilleurs délais ;</li>
          <li>tenons un <strong>registre des incidents de confidentialité</strong> conservé pour 5 ans.</li>
        </ol>
        <p>Pour signaler une faille ou un comportement suspect, contactez immédiatement notre RPRP à <a href={`mailto:${cfg.dpo_email}`} style={{ color: "#D4AF37" }}>{cfg.dpo_email}</a>.</p>
      </Section>

      <Section title="8. Vos droits — Accès, rectification, suppression, portabilité">
        <p>En vertu de la Loi 25, vous avez le droit :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>d'accéder</strong> à toutes les données vous concernant ;</li>
          <li><strong>de rectifier</strong> les données inexactes ;</li>
          <li><strong>de supprimer</strong> votre compte et l'ensemble de vos données ;</li>
          <li><strong>de recevoir vos données</strong> dans un format technologique structuré (droit à la portabilité — article 27) ;</li>
          <li><strong>de retirer votre consentement</strong> à tout moment ;</li>
          <li><strong>de porter plainte</strong> à la Commission d'accès à l'information.</li>
        </ul>
        <div className="p-4 rounded-xl mt-4" style={{ background: "rgba(74,222,128,0.06)", border: "1px solid rgba(74,222,128,0.2)" }}>
          <p style={{ color: "#F5F5F5" }}>
            Ces droits sont <strong>directement accessibles dans l'Application</strong> :
            rendez-vous dans <Link to="/app/settings" style={{ color: "#4ADE80" }}><strong>Paramètres → Mes droits (Loi 25)</strong></Link> pour exporter ou supprimer vos données en un clic.
          </p>
        </div>
        <p>Vous pouvez également exercer vos droits par écrit à <a href={`mailto:${cfg.dpo_email}`} style={{ color: "#D4AF37" }}>{cfg.dpo_email}</a>. Nous répondrons dans un délai maximal de <strong>30 jours</strong>.</p>
      </Section>

      <Section title="9. Conservation des données">
        <p>Nous conservons vos renseignements <strong>aussi longtemps que votre compte est actif</strong>. À la suppression du compte, toutes vos données sont effacées de manière irréversible dans un délai de 30 jours, à l'exception :</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>des informations comptables et de facturation conservées <strong>6 ans</strong> en vertu de la Loi de l'impôt sur le revenu du Canada ;</li>
          <li>du registre des incidents de confidentialité conservé <strong>5 ans</strong>.</li>
        </ul>
      </Section>

      <Section title="10. Témoins (cookies) et technologies similaires">
        <p>L'Application utilise un <strong>stockage local minimal</strong> dans votre navigateur (clé d'authentification JWT, préférence de langue, état de l'onboarding). Aucun témoin publicitaire ou de pistage tiers n'est utilisé.</p>
      </Section>

      <Section title="11. Mineurs">
        <p>L'Application est destinée à un usage professionnel. Nous ne sollicitons ni ne traitons sciemment de renseignements de personnes de moins de 14 ans.</p>
      </Section>

      <Section title="12. Modifications de cette politique">
        <p>Toute modification importante de cette politique sera notifiée par courriel à tous les utilisateurs au moins 30 jours avant son entrée en vigueur. La version en vigueur est toujours disponible à cette URL.</p>
      </Section>

      <div className="text-center mt-10 text-xs" style={{ color: "rgba(245,245,245,0.4)" }}>
        <Link to="/conditions" style={{ color: "#D4AF37" }}>{lang === "fr" ? "Voir les Conditions d'utilisation" : "View Terms of Service"}</Link>
      </div>
    </LegalShell>
  );
}
